import { useState } from "react";

import { orderRange } from "../../../calendar";
import { useFilterPanelContext } from "../../filter-panel.context";
import {
  dateRangeToValues,
  fromIsoDate,
  monthRangeToValues,
  toIsoDate,
  valuesToDateRange,
  valuesToMonthRange,
} from "../../filter-panel.model";
import {
  DATE_MODE_FIXED_LABEL,
  DATE_MODE_PERIOD_LABEL,
  FIELD_PLACEHOLDER,
  FILTER_EMPTY_MESSAGE,
  FILTER_ERROR_MESSAGE,
  FILTER_LOADING_MESSAGE,
  INCOMPLETE_ROW_MESSAGE,
  REMOVE_FILTER_LABEL,
  VALUES_PLACEHOLDER,
} from "../../messages";
import type { FilterDefinition, FilterRow } from "../../types-filter-panel";
import type {
  DateMode,
  FilterControl,
  FilterPanelRowProps,
  FilterPanelRowViewProps,
} from "./types-filter-row";

const DATE_MODE_OPTIONS = [
  { value: "fixed", label: DATE_MODE_FIXED_LABEL },
  { value: "period", label: DATE_MODE_PERIOD_LABEL },
];

function controlFor(
  definition: FilterDefinition,
  values: string[],
  fill: (next: string[]) => void,
  dateMode: DateMode | null,
  chooseDateMode: (mode: DateMode | null) => void,
): FilterControl {
  const options = definition.options ?? [];
  switch (definition.type) {
    case "select":
      return {
        kind: "select",
        options,
        value: values[0] ?? null,
        onChange: (event) => fill(event.value ? [event.value] : []),
      };
    case "multiselect":
      return {
        kind: "multiselect",
        options,
        value: values,
        onChange: (event) => fill(event.value ?? []),
      };
    case "date": {
      const start = fromIsoDate(values[0]);
      const end = fromIsoDate(values[1]);
      return {
        kind: "date",
        mode: dateMode,
        modeOptions: DATE_MODE_OPTIONS,
        onModeChange: (event) => {
          chooseDateMode((event.value as DateMode | null) ?? null);
          fill([]);
        },
        start,
        onStartChange: (event) => {
          const next = event.value instanceof Date ? event.value : null;
          if (dateMode !== "period") return fill(next ? [toIsoDate(next)] : []);
          if (!next) return fill([]);
          // O fim anterior ao novo início deixa de valer.
          fill([toIsoDate(next), end && end >= next ? toIsoDate(end) : ""]);
        },
        end,
        onEndChange: (event) => {
          const next = event.value instanceof Date ? event.value : null;
          if (start) fill([toIsoDate(start), next ? toIsoDate(next) : ""]);
        },
      };
    }
    case "date-range": {
      const dateRange = valuesToDateRange(values);
      return {
        kind: "date-range",
        value: dateRange[0] || dateRange[1] ? dateRange : null,
        onChange: (event) =>
          fill(
            dateRangeToValues(
              Array.isArray(event.value)
                ? orderRange(event.value[0] ?? null, event.value[1] ?? null)
                : [null, null],
            ),
          ),
      };
    }
    case "month-range":
      return {
        kind: "month-range",
        value: valuesToMonthRange(values),
        onChange: (range) => fill(monthRangeToValues(range)),
        empty: values.length === 0,
        start: () => fill(monthRangeToValues(valuesToMonthRange([]))),
      };
    case "text":
      return {
        kind: "text",
        value: values[0] ?? "",
        onChange: (event) => fill(event.target.value ? [event.target.value] : []),
      };
    default:
      return { kind: "none" };
  }
}

/** ViewModel de uma linha: seletor de campo, controle de valor conforme o tipo, erro e remoção; sem JSX. */
export function useFilterPanelRow({
  index,
  className,
  ...rest
}: FilterPanelRowProps): FilterPanelRowViewProps {
  const panel = useFilterPanelContext();
  const [pickedMode, setPickedMode] = useState<DateMode | null>(null);
  const row: FilterRow = panel.rows[index] ?? { id: -1, field: null };
  const field = row.field;
  const definition = field ? panel.definitionByKey.get(field) : undefined;
  const values = field ? (panel.draft[field] ?? []) : [];
  const error = panel.showErrors && panel.isIncomplete(row) ? INCOMPLETE_ROW_MESSAGE : null;
  const testId = panel.partTestId(`row-${index}`);
  // Filtro carregado ou aplicado já traz o modo: um valor é data fixa, dois são período.
  const dateMode: DateMode | null =
    pickedMode ?? (values.length === 2 ? "period" : values.length === 1 ? "fixed" : null);

  return {
    testId,
    className: [panel.classNames.row, className].filter(Boolean).join(" ") || undefined,
    rest,
    field: {
      value: field,
      options: panel.availableDefinitions(row).map(({ key, label }) => ({ value: key, label })),
      placeholder: FIELD_PLACEHOLDER,
      onChange: (event) => {
        setPickedMode(null);
        panel.chooseField(row, event.value ?? null);
      },
      testId: panel.partTestId(`column-${index}`),
    },
    control:
      definition && field
        ? controlFor(
            definition,
            values,
            (next) => panel.fillValues(field, next),
            dateMode,
            setPickedMode,
          )
        : { kind: "none" },
    controlTestId: panel.partTestId(`values-${index}`),
    base: {
      placeholder: definition?.isLoading ? FILTER_LOADING_MESSAGE : VALUES_PLACEHOLDER,
      emptyMessage: definition?.hasError ? FILTER_ERROR_MESSAGE : FILTER_EMPTY_MESSAGE,
      disabled: !definition || Boolean(definition.isLoading),
      searchPlaceholder: definition?.searchPlaceholder,
      ariaLabel: definition ? `Valores de ${definition.label}` : VALUES_PLACEHOLDER,
    },
    invalid: error !== null,
    error,
    errorTestId: `${testId}-error`,
    remove: {
      label: definition ? `${REMOVE_FILTER_LABEL} de ${definition.label}` : REMOVE_FILTER_LABEL,
      onClick: () => panel.removeRow(row),
      testId: panel.partTestId(`remove-${index}`),
    },
  };
}
